import {
  Accordion,
  ActionIcon,
  Alert,
  Box,
  Button,
  Divider,
  Group,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { AlertTriangle, Lock, Plus, Trash2 } from 'react-feather';
import { toDateValue } from '../../../../utils/dateHelpers';
import { DateTimeField } from '../../../../components/common';
import { DEFAULT_START_TIME_ON_DATE_PICK } from '../../../../utils/sessionEndTime';
import type { ConstraintRow, CoachHuddleRow, EditCohortForm, SessionDateRow, WrapDateConflict } from './editCohortTypes';

interface KeyDatesSectionProps {
  form: EditCohortForm;

  // Date constraints
  dateConstraints: ConstraintRow[];
  addDateConstraint: () => void;
  removeDateConstraint: (id: string) => void;
  updateDateConstraint: (id: string, field: keyof ConstraintRow, value: Date | null | string) => void;

  // Coach huddles
  coachHuddles: CoachHuddleRow[];
  addCoachHuddle: () => void;
  removeCoachHuddle: (id: string) => void;
  updateCoachHuddle: (id: string, dateTime: Date | null) => void;

  // Session dates
  sessionDates: SessionDateRow[];
  updateSessionDate: (sessionId: string, date: Date | null) => void;
  sessionDatesLocked: boolean;
  totalSessionCount: number;
  uniqueSessionNumbers: number[];

  // Wrap date conflict
  wrapDateConflict: WrapDateConflict | null;

  // CSS classes
  classes: Record<string, string>;
}

export function KeyDatesSection({
  form,
  dateConstraints,
  addDateConstraint,
  removeDateConstraint,
  updateDateConstraint,
  coachHuddles,
  addCoachHuddle,
  removeCoachHuddle,
  updateCoachHuddle,
  sessionDates,
  updateSessionDate,
  sessionDatesLocked,
  totalSessionCount,
  uniqueSessionNumbers,
  wrapDateConflict,
  classes,
}: KeyDatesSectionProps) {
  return (
    <Accordion.Item value="key-dates">
      <Accordion.Control>
        <Text fw={600}>Key Dates</Text>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="xl">
          {/* Date Constraints */}
          <Box>
            <Group justify="space-between" mb="sm">
              <Text size="sm" fw={500} c="dimmed">
                DATE CONSTRAINTS
              </Text>
              <Button variant="light" size="xs" leftSection={<Plus size={14} />} onClick={addDateConstraint}>
                Add Date Constraint
              </Button>
            </Group>
            {dateConstraints.length > 0 ? (
              <Stack gap="sm">
                {dateConstraints.map((constraint, index) => (
                  <Group key={constraint.id} className={classes.constraintRow}>
                    <DateInput
                      label={index === 0 ? 'Start Date' : undefined}
                      placeholder="Start date"
                      valueFormat="MM/DD/YYYY"
                      value={constraint.startDate}
                      onChange={(date) => updateDateConstraint(constraint.id, 'startDate', date)}
                      clearable
                      style={{ flex: 1 }}
                    />
                    <DateInput
                      label={index === 0 ? 'End Date' : undefined}
                      placeholder="End date"
                      valueFormat="MM/DD/YYYY"
                      value={constraint.endDate}
                      onChange={(date) => updateDateConstraint(constraint.id, 'endDate', date)}
                      minDate={constraint.startDate || undefined}
                      clearable
                      style={{ flex: 1 }}
                    />
                    <TextInput
                      label={index === 0 ? 'Label (optional)' : undefined}
                      placeholder="e.g., Holiday Break"
                      value={constraint.label}
                      onChange={(e) => updateDateConstraint(constraint.id, 'label', e.currentTarget.value)}
                      style={{ flex: 1.5 }}
                    />
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() => removeDateConstraint(constraint.id)}
                      mt={index === 0 ? 24 : 0}
                    >
                      <Trash2 size={18} />
                    </ActionIcon>
                  </Group>
                ))}
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">
                No date constraints added
              </Text>
            )}
          </Box>

          <Divider />

          {/* Cohort Dates */}
          <Box>
            <Text size="sm" fw={500} c="dimmed" mb="sm">
              COHORT DATES
            </Text>
            <Stack gap="md">
              <Group grow align="flex-end">
                <DateTimeField
                  defaultTimeOnDatePick={DEFAULT_START_TIME_ON_DATE_PICK}
                  label="Coach Immersion"
                  value={form.values.coach_immersion_date}
                  onChange={(value) => form.setFieldValue('coach_immersion_date', value)}
                />
                <DateTimeField
                  defaultTimeOnDatePick={DEFAULT_START_TIME_ON_DATE_PICK}
                  label="Participant Orientation"
                  value={form.values.participant_orientation_date}
                  onChange={(value) => form.setFieldValue('participant_orientation_date', value)}
                />
              </Group>

              <Group grow>
                <DateTimeField
                  defaultTimeOnDatePick={DEFAULT_START_TIME_ON_DATE_PICK}
                  label="Cohort Wrap Up"
                  value={form.values.cohort_wrap_up_date}
                  onChange={(value) => form.setFieldValue('cohort_wrap_up_date', value)}
                />
                <DateTimeField
                  defaultTimeOnDatePick={DEFAULT_START_TIME_ON_DATE_PICK}
                  label="Info Session Date"
                  value={form.values.info_session_date}
                  onChange={(value) => form.setFieldValue('info_session_date', value)}
                />
              </Group>

              <Group grow align="flex-end">
                <DateInput
                  label="Matching Form Deadline"
                  placeholder="Select date"
                  valueFormat="MM/DD/YYYY"
                  value={form.values.matching_form_deadline}
                  onChange={(date) => form.setFieldValue('matching_form_deadline', toDateValue(date))}
                  clearable
                />
                <Box /> {/* Spacer */}
              </Group>

              {wrapDateConflict && (
                <Alert icon={<AlertTriangle size={16} />} color="red" variant="light">
                  <Text size="sm">
                    Session dates extend past the cohort wrap date ({wrapDateConflict.wrapDateFormatted}). Please update
                    the cohort wrap date to{' '}
                    <Text span fw={600}>
                      {wrapDateConflict.suggestedDateFormatted}
                    </Text>{' '}
                    or later to accommodate all sessions.
                  </Text>
                </Alert>
              )}

              {/* Coach Huddles */}
              <Box>
                <Group justify="space-between" mb="xs">
                  <Text size="sm">Coach huddles</Text>
                  <Button variant="light" size="xs" leftSection={<Plus size={14} />} onClick={addCoachHuddle}>
                    Add coach huddle
                  </Button>
                </Group>
                {coachHuddles.length > 0 ? (
                  <Stack gap="xs">
                    {coachHuddles.map((huddle, index) => (
                      <Group key={huddle.id} align="flex-end">
                        <DateTimeField
                          defaultTimeOnDatePick={DEFAULT_START_TIME_ON_DATE_PICK}
                          placeholder={`Coach huddle ${index + 1}`}
                          value={huddle.dateTime}
                          onChange={(value) => updateCoachHuddle(huddle.id, value)}
                          style={{ flex: 1 }}
                        />
                        <ActionIcon color="red" variant="subtle" onClick={() => removeCoachHuddle(huddle.id)}>
                          <Trash2 size={18} />
                        </ActionIcon>
                      </Group>
                    ))}
                  </Stack>
                ) : (
                  <Text size="sm" c="dimmed">
                    No coach huddles scheduled
                  </Text>
                )}
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Session Week Dates */}
          <Box>
            <Group gap="xs" mb="sm">
              <Text size="sm" fw={500} c="dimmed">
                SESSION WEEK DATES
              </Text>
              <Text size="sm" c="dimmed">
                (optional - actual session dates will vary by group)
              </Text>
            </Group>

            {sessionDatesLocked ? (
              <Stack gap="md">
                <Alert
                  icon={<Lock size={18} />}
                  color="blue"
                  variant="light"
                  styles={{
                    root: { backgroundColor: 'var(--mantine-color-blue-0)' },
                    icon: { color: 'var(--mantine-color-blue-6)' },
                  }}
                >
                  <Text size="sm" fw={600} mb={4}>
                    Session dates are locked
                  </Text>
                  <Text size="sm" c="dimmed">
                    Sessions have been created for this cohort. Edit individual session dates and times from the{' '}
                    <Text span fw={600}>
                      Sessions tab
                    </Text>
                    .
                  </Text>
                </Alert>

                <Box>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" fw={500}>
                      Group Coaching
                    </Text>
                    <Text size="sm" c="dimmed">
                      {totalSessionCount} sessions
                    </Text>
                  </Group>
                  <Stack gap={4} pl="sm">
                    {uniqueSessionNumbers.map((num) => (
                      <Text key={num} size="sm" c="dimmed">
                        Session {num}
                      </Text>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            ) : sessionDates.length > 0 ? (
              <Stack gap="xs">
                {sessionDates.map((session) => (
                  <Group key={session.id} className={classes.sessionRow}>
                    <Text size="sm" fw={500} className={classes.sessionNumber}>
                      {session.groupName ? `${session.groupName} - ` : ''}Session {session.sessionNumber}
                    </Text>
                    <DateInput
                      placeholder="Select date"
                      valueFormat="MM/DD/YYYY"
                      value={session.date}
                      onChange={(date) => updateSessionDate(session.id, toDateValue(date))}
                      clearable
                      className={classes.sessionDateInput}
                    />
                  </Group>
                ))}
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">
                No sessions configured
              </Text>
            )}
          </Box>

          <Divider />

          {/* Announcement Email Config (MED-645) */}
          <Box>
            <Text size="sm" fw={500} c="dimmed" mb="sm">
              ANNOUNCEMENT EMAIL CONFIG
            </Text>
            <Stack gap="md">
              <Textarea
                label="Participant Description"
                placeholder="Brief description of participants for announcement emails"
                {...form.getInputProps('participant_description')}
                autosize
                minRows={2}
                maxRows={4}
              />
              <TextInput
                label="Matching Form Link"
                placeholder="https://..."
                {...form.getInputProps('matching_form_link')}
              />
            </Stack>
          </Box>
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}
